import { useState, useEffect } from 'react'
import { supabase } from './supabase'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import Auth from './Auth'
import { ajouterTampon as ajouterTamponAction, donnerRecompense, LiensLegaux } from './actions'
import ReglagesCarte from './ReglagesCarte'
import { exporterClientsCSV } from './export'
import { COMMERCE_DEMO, genererDemo } from './demo'
import './App.css'

const CORAL = '#E8522A'
const CHARBON = '#1C1C1C'

// ── Validation téléphone (format FR/international basique) ──────────────────
function isValidPhone(tel) {
  return /^(\+?\d[\s\-.]?){7,15}$/.test(tel.trim())
}

const MAX_AFFICHES = 15

// demo = true : tableau de bord rempli de données fictives, sans compte ni base (pour le terrain)
export default function App({ demo = false }) {
  const [donneesDemo] = useState(() => (demo ? genererDemo() : null))
  const [session, setSession]           = useState(demo ? { user: { id: 'demo' } } : null)
  const [commerce, setCommerce]         = useState(demo ? COMMERCE_DEMO : null)
  const [clients, setClients]           = useState(donneesDemo?.clients || [])
  const [visites, setVisites]           = useState(donneesDemo?.visites || [])
  const [newClient, setNewClient]       = useState({ nom: '', telephone: '' })
  const [formErrors, setFormErrors]     = useState({})
  const [ajoutOk, setAjoutOk]           = useState(false)
  const [walletLinks, setWalletLinks]   = useState({})
  const [loadingWallet, setLoadingWallet] = useState({})
  const [globalError, setGlobalError]   = useState('')
  const [clientHistorique, setClientHistorique] = useState(null) // id du client dont on voit l'historique
  const [rewardClient, setRewardClient] = useState(null) // client qui a atteint 10 tampons
  const [recherche, setRecherche]       = useState('')

  // ── Auth ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (demo) return
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      if (session) fetchCommerce(session.user.id)
    })
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session)
      if (session) fetchCommerce(session.user.id)
      else { setCommerce(null); setClients([]); setVisites([]) }
    })
    return () => subscription.unsubscribe()
  }, [demo])

  // ── Données temps réel ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!session || demo) return
    fetchClients()
    fetchVisites()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const channel = supabase
      .channel('realtime-visites')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'visites' }, () => {
        fetchVisites()
        fetchClients()
      })
      .subscribe()
    return () => supabase.removeChannel(channel)
  }, [session, demo]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch ───────────────────────────────────────────────────────────────────
  async function fetchCommerce(userId) {
    try {
      const { data, error } = await supabase
        .from('commerces')
        .select('*')
        .eq('id', userId)
        .maybeSingle()
      if (error) throw error
      if (data) {
        setCommerce(data)
        return
      }
      // Pas de profil commerce : on le crée automatiquement à partir du user
      const { data: userData } = await supabase.auth.getUser()
      const email = userData?.user?.email || ''
      const meta = userData?.user?.user_metadata || {}
      const defaults = {
        id: userId,
        nom: meta.nom_commerce || meta.nom || email.split('@')[0] || 'Mon commerce',
        type: meta.type_commerce || 'autre',
        email,
        telephone: meta.telephone || '',
      }
      const { data: inserted, error: insertErr } = await supabase
        .from('commerces')
        .insert(defaults)
        .select()
        .single()
      if (insertErr) throw insertErr
      setCommerce(inserted)
    } catch (err) {
      console.error('fetchCommerce error:', err)
      setGlobalError(
        "Impossible de charger le profil commerce. " +
        "Vérifie les policies RLS sur la table `commerces` (SELECT + INSERT doivent être autorisés pour `auth.uid() = id`)."
      )
    }
  }

  async function fetchClients() {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('commerce_id', session.user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      if (data) setClients(data)
    } catch (err) {
      setGlobalError('Impossible de charger la liste des clients.')
      console.error(err)
    }
  }

  async function fetchVisites() {
    try {
      const { data, error } = await supabase
        .from('visites')
        .select('*')
        .eq('commerce_id', session.user.id)
        .order('created_at', { ascending: false })
      if (error) throw error
      if (data) setVisites(data)
    } catch (err) {
      console.error('Erreur chargement visites:', err)
    }
  }

  // ── Actions ─────────────────────────────────────────────────────────────────
  async function ajouterTampon(clientId) {
    const client = clients.find(c => c.id === clientId)
    if (!client) return
    if (demo) {
      const n = (client.tampons || 0) + 1
      setClients(cs => cs.map(c => (c.id === clientId ? { ...c, tampons: n } : c)))
      setVisites(vs => [{ id: `demo-${Date.now()}`, client_id: clientId, commerce_id: 'demo', created_at: new Date().toISOString() }, ...vs])
      if (n >= objectif) setRewardClient({ ...client, tampons: n })
      return
    }
    try {
      const nouveauxTampons = await ajouterTamponAction(client, session.user.id)
      fetchClients()
      fetchVisites()
      if (nouveauxTampons >= objectif) {
        setRewardClient({ ...client, tampons: nouveauxTampons })
      }
    } catch (err) {
      setGlobalError('Erreur lors de l\'ajout du tampon.')
      console.error(err)
    }
  }

  async function resetTampons(clientId) {
    if (demo) {
      setClients(cs => cs.map(c => (c.id === clientId ? { ...c, tampons: 0 } : c)))
      setRewardClient(null)
      return
    }
    try {
      await donnerRecompense(clientId)
      setRewardClient(null)
      fetchClients()
    } catch (err) {
      setGlobalError('Erreur lors du reset des tampons.')
      console.error(err)
    }
  }

  async function ajouterClient() {
    const errors = {}
    if (!newClient.nom.trim()) errors.nom = 'Le nom est requis'
    if (newClient.telephone.trim() && !isValidPhone(newClient.telephone)) errors.telephone = 'Format invalide (ex: 06 12 34 56 78)'

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors)
      return
    }
    setFormErrors({})

    if (demo) {
      setClients(cs => [{ id: `demo-${Date.now()}`, nom: newClient.nom.trim(), telephone: newClient.telephone.trim() || null, tampons: 0, source: 'comptoir', created_at: new Date().toISOString() }, ...cs])
      setNewClient({ nom: '', telephone: '' })
      setAjoutOk(true)
      setTimeout(() => setAjoutOk(false), 2500)
      return
    }

    try {
      const { error } = await supabase.from('clients').insert({
        nom: newClient.nom.trim(),
        telephone: newClient.telephone.trim() || null,
        tampons: 0,
        commerce_id: session.user.id,
      })
      if (error) throw error
      setNewClient({ nom: '', telephone: '' })
      setAjoutOk(true)
      setTimeout(() => setAjoutOk(false), 2500)
      fetchClients()
    } catch (err) {
      setGlobalError('Erreur lors de l\'ajout du client.')
      console.error(err)
    }
  }

  async function genererCarteWallet(client) {
    if (demo) {
      setGlobalError('Démo : sur un vrai compte, ce bouton crée la carte Google Wallet du client.')
      return
    }
    setLoadingWallet(prev => ({ ...prev, [client.id]: true }))
    try {
      const { data, error } = await supabase.functions.invoke('generate-wallet-card', {
        body: { clientId: client.id, action: 'link' },
      })
      if (error) throw error
      if (data?.error) throw new Error(data.error)
      if (data?.url) {
        setWalletLinks(prev => ({ ...prev, [client.id]: data.url }))
      }
    } catch (err) {
      setGlobalError(`Carte Wallet : ${err.message}`)
      console.error('Erreur génération carte:', err)
    }
    setLoadingWallet(prev => ({ ...prev, [client.id]: false }))
  }

  // ── Déconnexion ─────────────────────────────────────────────────────────────
  async function handleSignOut() {
    if (demo) { window.location.href = '/'; return }
    await supabase.auth.signOut()
  }

  if (!session) return <Auth />

  const objectif = commerce?.objectif_tampons || 10

  // ── Métriques ────────────────────────────────────────────────────────────────
  const totalClients      = clients.length
  const visitesAujourdhui = visites.filter(v =>
    new Date(v.created_at).toDateString() === new Date().toDateString()
  ).length
  const clientsRisque = clients.filter(c => {
    const last = visites
      .filter(v => v.client_id === c.id)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
    if (!last) return false
    return Math.floor((new Date() - new Date(last.created_at)) / 86400000) >= 30
  }).length

  const debutSemaine = Date.now() - 7 * 86400000
  const visitesSemaine = visites.filter(v => new Date(v.created_at).getTime() >= debutSemaine)
  const jours = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
  const visitesParJour = jours.map((jour, i) => ({
    jour,
    visites: visitesSemaine.filter(v => new Date(v.created_at).getDay() === (i + 1) % 7).length
  }))

  const terme = recherche.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  const clientsAffiches = terme
    ? clients.filter(c => `${c.nom} ${c.telephone || ''}`.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').includes(terme))
    : clients

  // Historique du client sélectionné
  const visitesClient = clientHistorique
    ? visites
        .filter(v => v.client_id === clientHistorique)
        .slice(0, 8)
    : []

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div style={{
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
      background: '#f5f0eb',
      minHeight: '100vh',
      padding: 'clamp(1rem, 4vw, 2rem)'
    }}>

      {demo && (
        <div style={{ background: CHARBON, color: '#fff', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>👀 Démo avec des clients fictifs : clique sur +1, ajoute un client, personnalise la carte.</span>
          <a href="/app" style={{ color: '#fff', fontWeight: 700 }}>Créer mon compte →</a>
        </div>
      )}

      {/* Erreur globale */}
      {globalError && (
        <div style={{
          background: '#fff0ee', border: `1px solid ${CORAL}`, borderRadius: 10,
          padding: '10px 16px', marginBottom: 16, fontSize: 13, color: CORAL,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <span>⚠️ {globalError}</span>
          <button onClick={() => setGlobalError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: CORAL }}>×</button>
        </div>
      )}

      {/* Modal récompense */}
      {rewardClient && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem'
        }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: '2rem', maxWidth: 360, width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: CHARBON, marginBottom: 8 }}>
              {rewardClient.nom} a gagné sa récompense !
            </div>
            <div style={{ fontSize: 14, color: '#888', marginBottom: 24 }}>
              {objectif} tampons atteints : {commerce?.recompense || 'récompense'} à offrir, puis la carte repart à zéro.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setRewardClient(null)}
                style={{ flex: 1, background: '#f5f0eb', border: 'none', borderRadius: 10, padding: '10px', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: '#888' }}
              >
                Plus tard
              </button>
              <button
                onClick={() => resetTampons(rewardClient.id)}
                style={{ flex: 1, background: CORAL, color: '#fff', border: 'none', borderRadius: 10, padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              >
                Récompense donnée (0/{objectif})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '2rem', flexWrap: 'wrap', gap: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, background: CORAL, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="24" height="24" viewBox="0 0 100 100" fill="none">
              <rect x="10" y="14" width="44" height="26" rx="5" fill="#fff" opacity="0.5" transform="rotate(-5 32 27)"/>
              <rect x="14" y="8" width="44" height="26" rx="5" fill="#fff" opacity="0.9" transform="rotate(3 36 21)"/>
              <rect x="8" y="44" width="72" height="48" rx="10" fill="#fff" opacity="0.95"/>
              <circle cx="79" cy="67" r="5.5" fill={CORAL}/><circle cx="79" cy="67" r="2.5" fill="#fff"/>
            </svg>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: -1, color: CHARBON }}>jeebi</div>
            <div style={{ fontSize: 12, color: '#888' }}>{commerce?.nom || 'Mon commerce'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ background: '#EAF3DE', color: '#3B6D11', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>
            {totalClients} cartes actives
          </div>
          <button
            onClick={handleSignOut}
            style={{ background: 'transparent', border: '1px solid #e8e0d8', borderRadius: 8, padding: '6px 12px', fontSize: 13, color: '#888', cursor: 'pointer' }}
          >
            Déconnexion
          </button>
        </div>
      </div>

      {/* Métriques */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 12, marginBottom: '1.5rem'
      }}>
        {[
          { label: 'Clients total',       value: totalClients,      sub: 'cartes actives',    alert: false },
          { label: "Visites aujourd'hui", value: visitesAujourdhui, sub: 'passages scannés',  alert: false },
          { label: 'Clients à risque',    value: clientsRisque,     sub: 'absents +30 jours', alert: true  },
        ].map((m, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 12, padding: '1rem 1.25rem',
            border: m.alert && m.value > 0 ? `1px solid ${CORAL}` : '1px solid #e8e0d8'
          }}>
            <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>{m.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: m.alert && m.value > 0 ? CORAL : CHARBON }}>{m.value}</div>
            <div style={{ fontSize: 11, color: '#bbb', marginTop: 4 }}>{m.sub}</div>
          </div>
        ))}
      </div>

      {/* Graphique */}
      <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', marginBottom: '1.5rem', border: '1px solid #e8e0d8' }}>
        <div style={{ fontSize: 12, color: '#999', fontWeight: 500, marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Visites cette semaine
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={visitesParJour}>
            <XAxis dataKey="jour" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip />
            <Bar dataKey="visites" fill={CORAL} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Grille principale */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: 16
      }}>

        {/* Liste clients */}
        <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e8e0d8' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: 8 }}>
            <div style={{ fontSize: 12, color: '#999', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Clients ({totalClients})
            </div>
            {clients.length > 0 && (
              <button onClick={() => exporterClientsCSV(clients, visites, commerce?.nom)}
                style={{ background: 'transparent', border: '1px solid #e8e0d8', borderRadius: 8, padding: '4px 10px', fontSize: 12, color: '#666', cursor: 'pointer' }}>
                ⬇ Exporter (CSV)
              </button>
            )}
          </div>

          {clients.length > 0 && (
            <input
              type="search"
              placeholder="Rechercher un prénom ou un numéro…"
              value={recherche}
              onChange={e => setRecherche(e.target.value)}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #e8e0d8', fontSize: 14, boxSizing: 'border-box', marginBottom: 6 }}
            />
          )}

          {clients.length === 0 && (
            <div style={{ fontSize: 13, color: '#bbb' }}>Aucun client pour l'instant</div>
          )}
          {clients.length > 0 && clientsAffiches.length === 0 && (
            <div style={{ fontSize: 13, color: '#bbb', padding: '8px 0' }}>Aucun client ne correspond à « {recherche} »</div>
          )}

          {clientsAffiches.slice(0, MAX_AFFICHES).map(client => {
            const isRisque = (() => {
              const last = visites.filter(v => v.client_id === client.id).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0]
              if (!last) return false
              return Math.floor((new Date() - new Date(last.created_at)) / 86400000) >= 30
            })()

            return (
              <div key={client.id}>
                <div style={{ padding: '10px 0', borderBottom: '1px solid #f5f0eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: '50%', background: '#f5f0eb',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 12, fontWeight: 700, color: CORAL, flexShrink: 0,
                      cursor: 'pointer'
                    }} onClick={() => setClientHistorique(clientHistorique === client.id ? null : client.id)}>
                      {client.nom.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: CHARBON, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {client.nom}
                        </div>
                        {isRisque && (
                          <span style={{ fontSize: 10, background: '#fff0ee', color: CORAL, borderRadius: 4, padding: '1px 5px', fontWeight: 600, flexShrink: 0 }}>
                            À risque
                          </span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: 3, marginTop: 4, flexWrap: 'wrap' }}>
                        {Array.from({ length: objectif }).map((_, i) => (
                          <div key={i} style={{
                            width: 11, height: 11, borderRadius: 3,
                            background: i < (client.tampons || 0) ? CORAL : '#f0ece8'
                          }} />
                        ))}
                        <span style={{ fontSize: 11, color: '#bbb', marginLeft: 4 }}>
                          {client.tampons || 0}/{objectif}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => ajouterTampon(client.id)}
                      style={{
                        background: CORAL, color: '#fff', border: 'none', borderRadius: 8,
                        padding: '6px 10px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        flexShrink: 0
                      }}
                    >
                      +1
                    </button>
                  </div>

                  {/* Bouton Wallet */}
                  <div style={{ marginTop: 8, paddingLeft: 42 }}>
                    {walletLinks[client.id] ? (
                      <a
                        href={walletLinks[client.id]}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                          background: CHARBON, color: '#fff', borderRadius: 8,
                          padding: '5px 12px', fontSize: 12, fontWeight: 600, textDecoration: 'none'
                        }}
                      >
                        Ouvrir carte Wallet
                      </a>
                    ) : (
                      <button
                        onClick={() => genererCarteWallet(client)}
                        disabled={loadingWallet[client.id]}
                        style={{
                          background: '#f5f0eb', color: '#888', border: '1px solid #e8e0d8',
                          borderRadius: 8, padding: '5px 12px', fontSize: 12, cursor: loadingWallet[client.id] ? 'wait' : 'pointer'
                        }}
                      >
                        {loadingWallet[client.id] ? 'Génération...' : '+ Générer carte Wallet'}
                      </button>
                    )}
                  </div>
                </div>

                {/* Historique des visites (dépliable) */}
                {clientHistorique === client.id && (
                  <div style={{
                    background: '#faf7f5', borderRadius: 8, padding: '10px 12px',
                    margin: '8px 0', fontSize: 12
                  }}>
                    <div style={{ fontWeight: 600, color: '#888', marginBottom: 6 }}>
                      Dernières visites
                    </div>
                    {visitesClient.length === 0 ? (
                      <div style={{ color: '#bbb' }}>Aucune visite enregistrée</div>
                    ) : (
                      visitesClient.map(v => (
                        <div key={v.id} style={{ color: '#666', padding: '3px 0', borderBottom: '1px solid #f0ece8' }}>
                          {new Date(v.created_at).toLocaleDateString('fr-FR', {
                            weekday: 'short', day: 'numeric', month: 'short',
                            hour: '2-digit', minute: '2-digit'
                          })}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {clientsAffiches.length > MAX_AFFICHES && (
            <div style={{ fontSize: 12, color: '#999', paddingTop: 8 }}>
              … et {clientsAffiches.length - MAX_AFFICHES} autres. Tape un prénom pour les retrouver.
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Ajouter client */}
          <div style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e8e0d8' }}>
            <div style={{ fontSize: 12, color: '#999', fontWeight: 500, marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Ajouter un client
            </div>
            <div style={{ marginBottom: 8 }}>
              <input
                placeholder="Nom du client *"
                value={newClient.nom}
                onChange={e => { setNewClient({ ...newClient, nom: e.target.value }); setFormErrors(f => ({ ...f, nom: '' })) }}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${formErrors.nom ? CORAL : '#e8e0d8'}`,
                  fontSize: 14, boxSizing: 'border-box'
                }}
              />
              {formErrors.nom && <div style={{ color: CORAL, fontSize: 11, marginTop: 3 }}>{formErrors.nom}</div>}
            </div>
            <div style={{ marginBottom: 12 }}>
              <input
                placeholder="Téléphone (facultatif)"
                value={newClient.telephone}
                onChange={e => { setNewClient({ ...newClient, telephone: e.target.value }); setFormErrors(f => ({ ...f, telephone: '' })) }}
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 8,
                  border: `1px solid ${formErrors.telephone ? CORAL : '#e8e0d8'}`,
                  fontSize: 14, boxSizing: 'border-box'
                }}
              />
              {formErrors.telephone && <div style={{ color: CORAL, fontSize: 11, marginTop: 3 }}>{formErrors.telephone}</div>}
            </div>
            <button
              onClick={ajouterClient}
              style={{ width: '100%', background: CHARBON, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              Ajouter
            </button>
            {ajoutOk && <div style={{ fontSize: 13, color: '#3B6D11', marginTop: 8 }}>✅ Client ajouté !</div>}
          </div>

          {/* Ma carte de fidélité */}
          <ReglagesCarte
            commerce={commerce}
            session={session}
            onEnregistre={setCommerce}
            onErreur={setGlobalError}
            demo={demo}
          />

        </div>
      </div>
      <LiensLegaux style={{ marginTop: '2rem' }} />
    </div>
  )
}
